import { endOfWeek, formatDate, startOfWeek } from "date-fns"
import { getMixedPalette } from "design-system/lib/chart-utils"
import { Card } from "design-system/molecules/Card"
import { DateRange } from "design-system/molecules/DatePicker"
import { NoData } from "design-system/molecules/NoData"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { useGoalsQuery } from "modules/campaigns/hooks/sections/goals/useGoalsQuery"
import { useCanonicalTopicsTimelineQuery } from "modules/insights/hooks/useCanonicalTopicsTimelineQuery"
import { usePredefinedSegmentGroupsTranslation } from "modules/segments/hooks/usePredefinedSegmentGroupsTranslation"
import { usePredefinedSegmentTranslations } from "modules/segments/hooks/usePredefinedSegmentTranslations"
import { useSegmentGroupsQuery } from "modules/segments/hooks/useSegmentGroupsQuery"
import { formatCanonicalTopicLabel } from "modules/shared/lib/formatCanonicalTopicLabel"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ClusterDomainEnum,
  ClusterDomainSchema,
  ClusterSegmentEnum,
  ClusterSegmentSchema,
  UNCATEGORIZED_CANONICAL_TOPIC_ID,
} from "schema"
import { z } from "zod"
import { CanonicalTopicsRankingChart } from "./CanonicalTopicsRankingChart"
import { CanonicalTopicsTooltip } from "./CanonicalTopicsTooltip"

const SUPPORT_PALETTE_DOMAINS: z.infer<typeof ClusterDomainSchema>[] = [
  ClusterDomainEnum.Support,
  ClusterDomainEnum.Issue,
  ClusterDomainEnum.Question,
  ClusterDomainEnum.ActionRequest,
]

type OnApplyFiltersProps = {
  domain: z.infer<typeof ClusterDomainSchema> | undefined
  segmentId: string | undefined
  goalId: string | undefined
  canonicalId: string | undefined
  dateRange: DateRange | undefined
}

type Props = {
  availableDomains: z.infer<typeof ClusterDomainSchema>[]
  domain: z.infer<typeof ClusterDomainSchema> | undefined
  goalId?: string
  segmentId: string | undefined
  canonicalId: string | undefined
  dateRange: DateRange | undefined
  groupNonCategoryTopics?: boolean
  onApplyFilters?: ((props: OnApplyFiltersProps) => void) | undefined
}

export const CanonicalTopicsTimeline = ({
  availableDomains,
  domain: domainProp,
  segmentId: segmentIdProp,
  goalId: goalIdProp,
  canonicalId: canonicalIdProp,
  dateRange: dateRangeProp,
  groupNonCategoryTopics = false,
  onApplyFilters,
}: Props) => {
  const { t } = useTranslation()

  const { data: segmentGroups = [] } = useSegmentGroupsQuery()
  const { getSegmentGroupTranslation } = usePredefinedSegmentGroupsTranslation()
  const { getSegmentTranslation } = usePredefinedSegmentTranslations()
  const { data: goals = [] } = useGoalsQuery({
    query: {
      total: 10,
      sortBy: { field: "name", order: "asc" },
      filterBy: {
        withCanonicalTopics: "true",
      },
    },
  })

  const [domain, setDomain] = useState<z.infer<typeof ClusterDomainSchema>>(
    domainProp ?? ClusterDomainEnum.VoiceOfCustomer,
  )
  const [segmentId, setSegmentId] = useState<string | undefined>(
    segmentIdProp ?? ClusterSegmentEnum.all,
  )
  const [goalId, setGoalId] = useState<string | undefined>(goalIdProp ?? "all")
  const [canonicalId, setCanonicalId] = useState<string | undefined>(canonicalIdProp)
  const [dateRange, setDateRange] = useState<DateRange | undefined>(dateRangeProp)

  useEffect(() => {
    if (domain === ClusterDomainEnum.Goal && goals.length > 0 && (!goalId || goalId === "all")) {
      const firstGoal = goals[0]
      if (firstGoal) {
        setGoalId(firstGoal.id)
      }
    }
    if (domain === ClusterDomainEnum.Goal && goals.length === 0) {
      setGoalId(undefined)
    }
  }, [domain, goals, goalId])

  const querySegmentId =
    domain === ClusterDomainEnum.Goal || segmentId === ClusterSegmentEnum.all
      ? undefined
      : segmentId

  const queryGoalId = domain === ClusterDomainEnum.Goal && goalId !== "all" ? goalId : undefined

  const { data, isPending } = useCanonicalTopicsTimelineQuery({
    query: {
      domain,
      segmentId: querySegmentId,
      goalId: queryGoalId,
      groupNonCategoryTopics,
    },
  })

  const canApplyFilters = useMemo(() => {
    if (domain !== domainProp) return true
    if (domain === ClusterDomainEnum.Goal) {
      if (goalIdProp && goalId !== goalIdProp) return true
      if (!goalIdProp && goalId && goalId !== "all") return true
    } else {
      if (segmentIdProp && segmentId !== segmentIdProp) return true
      if (!segmentIdProp && segmentId !== ClusterSegmentEnum.all) return true
    }
    if (canonicalId !== canonicalIdProp) return true
    if (
      formatDate(dateRange?.from ?? new Date(), "yyyy-MM-dd") !==
      formatDate(dateRangeProp?.from ?? new Date(), "yyyy-MM-dd")
    )
      return true
    if (
      formatDate(dateRange?.to ?? new Date(), "yyyy-MM-dd") !==
      formatDate(dateRangeProp?.to ?? new Date(), "yyyy-MM-dd")
    )
      return true
    return false
  }, [
    domain,
    domainProp,
    segmentId,
    segmentIdProp,
    goalId,
    goalIdProp,
    canonicalId,
    canonicalIdProp,
    dateRange,
    dateRangeProp,
  ])

  const canonicalOptions = useMemo(() => {
    if (!data) return []

    // Extract all unique canonical topics from timeline data
    const topicMap = new Map<string, { id: string; label: string; description?: string | null }>()

    // Collect topics from timeline rankings
    data.timeline.forEach((period) => {
      period.rankings.forEach((ranking) => {
        if (!topicMap.has(ranking.canonicalTopicId)) {
          topicMap.set(ranking.canonicalTopicId, {
            id: ranking.canonicalTopicId,
            label: formatCanonicalTopicLabel(ranking.label),
            description: null, // Timeline rankings don't include description
          })
        }
      })
    })

    // Collect topics from overall ranking (includes descriptions)
    Object.values(data.overallRanking).forEach((topic) => {
      const existing = topicMap.get(topic.canonicalTopicId)
      if (existing) {
        existing.description = topic.description
      } else {
        topicMap.set(topic.canonicalTopicId, {
          id: topic.canonicalTopicId,
          label: formatCanonicalTopicLabel(topic.label),
          description: topic.description,
        })
      }
    })

    const topics = Array.from(topicMap.values()).filter(
      (topic) => topic.id !== UNCATEGORIZED_CANONICAL_TOPIC_ID,
    )

    const options = topics.map((t) => {
      const topicId = t.id
      const baseLabel = t.label

      // Add rank to label if available
      if (data?.overallRanking?.[topicId]?.rank !== undefined) {
        const rank = data.overallRanking[topicId].rank
        return { key: topicId, label: `#${rank} - ${baseLabel}` }
      }

      return { key: topicId, label: baseLabel }
    })

    // If overallRanking is available, sort options to prioritize ranked topics
    if (data?.overallRanking) {
      const rankingMap = data.overallRanking

      return options.sort((a, b) => {
        const rankA = rankingMap[a.key]?.rank ?? Infinity
        const rankB = rankingMap[b.key]?.rank ?? Infinity

        // Topics with ranking come first, sorted by rank
        // Topics without ranking come after, sorted alphabetically
        if (rankA === Infinity && rankB === Infinity) {
          return a.label.localeCompare(b.label)
        }
        if (rankA === Infinity) return 1
        if (rankB === Infinity) return -1
        return rankA - rankB
      })
    }

    return options
  }, [data])

  const canonicalColorMap = useMemo(() => {
    if (!data) return {}
    const map: Record<string, string> = {}
    const palette = getMixedPalette(SUPPORT_PALETTE_DOMAINS.includes(domain))
    const entries = Object.entries(data.overallRanking ?? {}).sort(
      (a, b) => (a[1].rank ?? 0) - (b[1].rank ?? 0),
    )

    // Assign unique palette colors only to the top N overall topics.
    const maxColored = Math.min(palette.length, entries.length)
    for (let i = 0; i < maxColored; i += 1) {
      const entry = entries[i]
      if (!entry) continue
      const [canonicalId] = entry
      map[String(canonicalId)] = palette[i] ?? "neutral"
    }
    return map
  }, [data, domain])

  const [hoveredNodeId, setHoveredNodeId] = useState<string | undefined>(undefined)
  const [selectedNodeId, setSelectedNodeId] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!domainProp || domainProp === domain) return

    setDomain(domainProp)
    setSelectedNodeId(undefined)
    setHoveredNodeId(undefined)
  }, [domain, domainProp])

  // Keep local selectedTopicId in sync with external selectedCanonicalId changes
  useEffect(() => {
    const expectedTopicId = canonicalId === "all" ? undefined : canonicalId
    if (canonicalId !== expectedTopicId) {
      setCanonicalId(expectedTopicId)
    }
  }, [canonicalId])

  // Unselect canonicalId when is not available
  useEffect(() => {
    if (!data?.overallRanking) return

    if (!canonicalId) return
    if (data?.overallRanking?.[canonicalId]) return

    setCanonicalId(undefined)
  }, [domain, canonicalId, onApplyFilters, data?.overallRanking])

  function onSelectNodeId(nodeId: string | undefined) {
    if (nodeId?.endsWith(`-${UNCATEGORIZED_CANONICAL_TOPIC_ID}`)) return

    const isSelecting = nodeId !== selectedNodeId

    if (!isSelecting) {
      setSelectedNodeId(undefined)
      setHoveredNodeId(undefined)
      setDateRange(undefined)
    } else {
      setSelectedNodeId(nodeId)
    }

    const previousDateFrom = dateRange?.from

    if (nodeId) {
      const lastDash = nodeId.lastIndexOf("-")

      if (lastDash > 0 && lastDash < nodeId.length - 1) {
        const topicIdFromNode = nodeId.slice(lastDash + 1)
        const dateISO = nodeId.slice(0, lastDash)

        setCanonicalId(topicIdFromNode)

        if (
          dateISO &&
          formatDate(dateISO, "yyyy-MM-dd") !==
            (previousDateFrom && formatDate(previousDateFrom, "yyyy-MM-dd"))
        ) {
          const weekStart = startOfWeek(new Date(dateISO), { weekStartsOn: 1 })
          const weekEnd = endOfWeek(new Date(dateISO), { weekStartsOn: 1 })

          setDateRange({
            from: weekStart,
            to: weekEnd,
          })
        } else {
          setDateRange(undefined)
        }
      }
    } else {
      setCanonicalId(undefined)
      setDateRange(undefined)
    }
  }

  function onSelectTopicId(topicId: string | undefined) {
    if (topicId === UNCATEGORIZED_CANONICAL_TOPIC_ID) return

    if (topicId === canonicalId) {
      setCanonicalId(undefined)
    } else {
      setCanonicalId(topicId)
    }
  }

  return (
    <Card className="flex flex-col p-6 gap-2">
      <div className="flex flex-col md:flex-row gap-4 md:gap-2 w-full">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4 justify-between w-full">
          <div className="flex flex-1 basis-64 flex-col items-start gap-1">
            <div className="font-medium text-md">{t("insights.canonicalTimeline.title")}</div>
            <div className="font-normal text-sm text-muted-foreground">
              {t("insights.canonicalTimeline.description")}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center sm:justify-end gap-2 w-full sm:w-auto">
            {availableDomains.length > 1 && (
              <Select
                value={domain}
                onValueChange={(value: z.infer<typeof ClusterDomainSchema>) => {
                  setDomain(value)
                  if (value === ClusterDomainEnum.Goal) {
                    setSegmentId(ClusterSegmentEnum.all)
                    const firstGoal = goals.length > 0 && goals[0] ? goals[0].id : "all"
                    setGoalId(firstGoal)
                  } else {
                    setGoalId("all")
                    setSegmentId(ClusterSegmentEnum.all)
                  }
                  setSelectedNodeId(undefined)
                }}
              >
                <SelectTrigger className="w-full sm:w-44">
                  <SelectValue placeholder="Select domain" />
                </SelectTrigger>
                <SelectContent>
                  {availableDomains.map((d) => (
                    <SelectItem key={d} value={d}>
                      {t(`dashboard.domains.${d}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {domain === ClusterDomainEnum.Goal ? (
              <Select
                value={goalId}
                onValueChange={(value: string) => {
                  setGoalId(value)
                  setSelectedNodeId(undefined)
                }}
                disabled={goals.length === 0}
              >
                <SelectTrigger className="lg:w-72">
                  <SelectValue
                    placeholder={
                      goals.length === 0 ? t("insights.canonicalTimeline.noGoals") : undefined
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {goals.map((goal) => (
                    <SelectItem key={goal.id} value={goal.id}>
                      {goal.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Select
                value={segmentId}
                onValueChange={(value: z.infer<typeof ClusterSegmentSchema>) => {
                  setSegmentId(value)
                  setSelectedNodeId(undefined)
                }}
              >
                <SelectTrigger className="lg:w-44">
                  <SelectValue placeholder="all" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ClusterSegmentEnum.all}>
                    {t("dashboard.filters.allSegments")}
                  </SelectItem>
                  {segmentGroups.map((segmentGroup) => (
                    <SelectGroup key={segmentGroup.id}>
                      <SelectLabel>{getSegmentGroupTranslation(segmentGroup)}</SelectLabel>
                      {segmentGroup.segments.map((segment) => (
                        <SelectItem key={segment.id} value={segment.id} className="pl-4">
                          {getSegmentTranslation(segment.name, segmentGroup)}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select
              value={canonicalId ?? "all"}
              onValueChange={(value: string) => {
                setCanonicalId(value)
                setSelectedNodeId(undefined)
              }}
            >
              <SelectTrigger className="lg:w-72">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("insights.canonicalTimeline.filters.allTopics")}
                </SelectItem>
                {canonicalOptions.map((item) => (
                  <SelectItem key={item.key} value={item.key}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      {!isPending && data?.timeline.length === 0 && (
        <NoData
          frame
          title={t("insights.canonicalTimeline.noData.title")}
          description={t("insights.canonicalTimeline.noData.description")}
          className="mt-2"
        />
      )}
      {!isPending && data && data?.timeline.length > 0 && (
        <div className="flex flex-col lg:flex-row gap-4 w-full">
          <div className="w-full lg:flex-1 min-w-0 pt-6">
            <CanonicalTopicsRankingChart
              data={data}
              canonicalColorMap={canonicalColorMap}
              isPending={isPending}
              selectedTopicId={canonicalId}
              selectedNodeId={selectedNodeId}
              onHoverNodeId={(nodeId) => {
                if (typeof nodeId === "string" && nodeId.includes("-")) {
                  setHoveredNodeId(nodeId)
                } else {
                  setHoveredNodeId(undefined)
                }
              }}
              onSelectNodeId={onSelectNodeId}
              onSelectTopicId={onSelectTopicId}
            />
          </div>
          <CanonicalTopicsTooltip
            data={data}
            isSupport={domain === ClusterDomainEnum.Support}
            canonicalColorMap={canonicalColorMap}
            hoveredNodeId={hoveredNodeId}
            selectedNodeId={selectedNodeId}
            selectedTopicId={canonicalId}
            onSelectNodeId={onSelectNodeId}
            onSelectTopicId={onSelectTopicId}
            onApplyFilters={
              onApplyFilters
                ? () => {
                    const filterSegmentId =
                      domain === ClusterDomainEnum.Goal || segmentId === ClusterSegmentEnum.all
                        ? undefined
                        : segmentId

                    const filterGoalId =
                      domain === ClusterDomainEnum.Goal && goalId !== "all" ? goalId : undefined

                    onApplyFilters({
                      domain,
                      segmentId: filterSegmentId,
                      goalId: filterGoalId,
                      canonicalId,
                      dateRange,
                    })
                  }
                : undefined
            }
            canApplyFilters={canApplyFilters}
          />
        </div>
      )}
    </Card>
  )
}
